import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from '@shared/hooks/useStore';
import { Button } from '@shared/components/common/Button';
import Heading from '@shared/components/common/Heading';
import { HelpCircle } from 'lucide-react';
import Pagination from '@shared/components/pagination/Pagination';
import { Package } from '@shared/types/package';
import { PACKAGE_TYPE } from '@shared/types/enums';
import { formatDateTime } from '@shared/utils/datetime';
import { DATE_YMD } from '@shared/components/constants/common.constants';
import defaultCover from '@shared/assets/images/default-cover.png';
import Tooltip from '@shared/components/common/Tooltip';

interface PlanDetailProps {
    plan?: Package,
    onClose: () => void
}

const InfoRow = ({ label, value, showTooltip = false }: { label: string, value: string | number, showTooltip?: boolean }) => (
    <div className='flex flex-col md:flex-row leading-[21px]'>
        <div className='w-full md:w-1/2 min-w-[200px] text-[#999999]'>
            {label}
        </div>
        {showTooltip ?
            (
                <Tooltip
                    content={value}
                    triggerAsChild
                    triggerClassName='w-fit'
                    contentClassName='max-w-[320px] whitespace-pre-line text-break'
                >
                    <div className='flex-1 line-clamp-2'>
                        {value}
                    </div>
                </Tooltip>
            ) :
            (
                <div className='flex-1 line-clamp-2'>
                    {value}
                </div>
            )}
    </div>
);

const PublicationItem = ({ item, index }: { item: any, index: number }) => (
    <div className={`flex flex-col md:flex-row items-center gap-1 p-3 ${index % 2 === 1 ? 'bg-[#F1F5F9]' : ''}`}>
        <div className='w-[74px] h-[100px] md:w-[37px] md:h-[50px] bg-gray-200 flex-shrink-0'>
            <img
                src={item.linkImg}
                onError={(e) => {
                    e.currentTarget.src = defaultCover;
                }}
                alt='cover'
                className='w-full h-full object-cover'
            />
        </div>
        <div className='flex-1 grid grid-cols-1 md:grid-cols-3 gap-6 w-full leading-[21px]'>
            <div className='col-span-1 lg:col-span-1'>
                <div className='text-[#999999] mb-1'>春号</div>
                <Tooltip content={item.code} triggerAsChild contentClassName='max-w-[320px] whitespace-pre-line text-break'>
                    <div className='line-clamp-1'>{item.code}</div>
                </Tooltip>
            </div>
            <div className='col-span-1 lg:col-span-1'>
                <div className='text-[#999999] mb-1'>既読ユーザー数</div>
                <div>{item.readCount}</div>
            </div>
            <div className='col-span-1 lg:col-span-1'>
                <div className='text-[#999999] mb-1 flex items-center gap-1'>
                    既読率
                    {' '}
                    <Tooltip
                        content='本コンテンツを閲覧したユーザー数 / プラン内全書籍・雑誌の延べ閲覧ユーザー数'
                        triggerAsChild
                        contentClassName='max-w-[240px]'
                    >
                        <HelpCircle size={14} className='text-[#999999]' />
                    </Tooltip>
                </div>
                <div>
                    {item.readRate}
                    %
                </div>
            </div>
        </div>
    </div>
);

export default observer(function PlanDetailModal({ plan, onClose }: PlanDetailProps) {
    const { groupAdminStore } = useStore();
    const { packageItems, totalPackageItems, loading } = groupAdminStore;

    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    useEffect(() => {
        if (plan?.id) {
            groupAdminStore.fetchPackageItems(plan.id.toString(), {
                page: currentPage,
                size: pageSize
            });
        }
    }, [plan?.id, currentPage, groupAdminStore]);

    return (
        <div className='w-full bg-white sm:rounded-lg overflow-hidden flex flex-col h-full sm:h-auto sm:max-h-[90vh] text-break'>
            {/* Header */}
            <div className='bg-[#E6F9EE] py-4 md:py-10 flex items-center justify-center relative shrink-0'>
                <Heading className='text-center text-xl md:text-2xl font-bold'>購入プランの情報</Heading>
            </div>

            <div className='px-4 md:px-8 py-4 md:py-6 space-y-[48px] overflow-y-auto flex-1'>
                {/* Plan Info Section */}
                <div className='space-y-[24px]'>
                    <InfoRow label='プラン名' value={plan?.name || '--'} showTooltip />
                    <InfoRow label='プランコード' value={plan?.code || '--'} showTooltip />
                    <InfoRow label='プラン種別' value={plan?.packageType === PACKAGE_TYPE.UNLIMITED ? '読み放題プラン' : plan?.packageType === PACKAGE_TYPE.SPECIFIED ? '購入冊数プラン' : plan?.packageType || '--'} />
                    <InfoRow label='書籍／雑誌数' value={plan?.packageType === PACKAGE_TYPE.UNLIMITED ? '無制限' : (plan?.numberPublications || 0) || '--'} />
                    <InfoRow label='説明' value={plan?.description || '--'} showTooltip />
                    <InfoRow label='プランの利用期間' value={`${formatDateTime(plan?.startDate, DATE_YMD, { returnDefault: '未定' })} - ${!plan?.hasExpiration ? '無制限' : formatDateTime(plan?.expirationDate, DATE_YMD, { returnDefault: '未定' })}`} />
                </div>

                {/* Items List Section */}
                <div className='border border-[#E7E7E7] rounded-[4px] p-3 relative min-h-[200px]'>
                    {packageItems.length > 0 ?
                        packageItems.map((item: any, index: number) => (
                            <PublicationItem key={item.id || index} item={item} index={index} />
                        )) :
                        !loading && (
                            <div className='text-center py-4 text-gray-500'>アイテムがありません。</div>
                        )}
                </div>

                {totalPackageItems > pageSize && (
                    <div className='flex justify-center py-4'>
                        <Pagination
                            type='full-pagination'
                            paginationClassName='w-fit'
                            currentPage={currentPage}
                            totalRecords={totalPackageItems}
                            pageSize={pageSize}
                            onPageChange={setCurrentPage}
                        />
                    </div>
                )}
            </div>

            {/* Close Button */}
            <div className='flex justify-center p-6 shrink-0 bg-white'>
                <Button
                    onClick={onClose}
                    className='w-full md:w-[358px] h-[56px] bg-primary hover:bg-[#005230] text-white rounded-[10px]'
                >
                    閉じる
                </Button>
            </div>
        </div>
    );
});
